import ContentCopy from '@ixigo/icons/ContentCopy';
import Button from '@ixigo/iui/Button';
import { toast } from '@ixigo/iui/Toast';
import cn from '@repo/shared/utils/cn';
import { Fragment, useCallback } from 'react';
import type { CopyToClipboardProps } from './index.types';
import { useClipboard } from './useClipboard';

export const getToastPosition = {
	containerStyle: {
		bottom: 90,
		left: 20,
		right: 20,
	},
};

const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
	content = '',
	className = '',
	title = 'Copy',
	titleSize = 'body-xs',
	icon = <ContentCopy fontSize={20} className="text-secondary" />,
	successMessage = 'Copied to clipboard!',
	errorMessage = 'Failed to copy to clipboard',
	children,
	onCopySuccess,
	onCopyError,
	placement = 'normal',
}) => {
	const toastProps = {
		duration: 3000,
		isModal: placement === 'modal',
	};
	const { copyToClipboard } = useClipboard({
		successCallback: () => {
			toast.primary(successMessage, {
				...toastProps,
				...getToastPosition,
			});
			onCopySuccess?.();
		},
		errorCallback: () => {
			toast.error(errorMessage, {
				...toastProps,
				...getToastPosition,
			});
			onCopyError?.();
		},
	});

	const handleCopy = useCallback(async () => {
		await copyToClipboard(content);
	}, [content, copyToClipboard]);

	return (
		<Fragment>
			{children || (
				<Button
					variant="flat"
					size="sm"
					className={cn(
						'cursor-copy !p-0 !min-h-fit hover:bg-transparent',
						className,
						titleSize,
					)}
					onClick={handleCopy}
					endAdornment={icon}
					aria-label={title}
				>
					{title}
				</Button>
			)}
		</Fragment>
	);
};

export default CopyToClipboard;
